import { createComplaint } from "../../utils/zulalWebAPI";
import { PhotoIcon } from "@heroicons/react/24/outline";
import React, { useState, useContext } from "react";
import { UserContext } from "../../context/user.context";
import { toast } from "react-toastify";

export const ComplaintPage = () => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const { currentUser } = useContext(UserContext);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
    const allowedTypes = ["image/jpeg", "image/png"];

    if (selectedFile) {
      if (!allowedTypes.includes(selectedFile.type)) {
        setError("Only JPG and PNG files are allowed");
        setFile(null);
        toast.error("Only JPG and PNG files are allowed");
        return;
      }

      if (selectedFile.size > maxFileSize) {
        setError("File size exceeds 5MB");
        setFile(null);
        toast.error("File size exceeds 5MB");
        return;
      }

      setError("");
      if (selectedFile) {
        const originalName = selectedFile.name;
        const dotIndex = originalName.lastIndexOf(".");

        // console.log("last index of .", dotIndex);

        const fileExt = originalName.substring(dotIndex); // includes the dot, e.g. ".png"
        // console.log("file ext", fileExt);
        const fixedFileName = `${currentUser.customerCode}${fileExt}`;
        // console.log("fixed file name", fixedFileName);
        const fixedFile = new File([selectedFile], fixedFileName, {
          type: selectedFile.type,
        });

        setMessage(`File selected: ${fixedFile.name}`); // Show the fixed file name
        toast.success(`File selected: ${fixedFile.name}`);
        setFile(fixedFile);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(""); // clear any previous messages
    setError(""); // clear previous errors

    try {
      const response = await createComplaint({ description, file });

      if (response.success) {
        // Show reference number if available
        const ref = response.data?.referenceNumber;
        setMessage(
          ref
            ? `Complaint submitted. Reference: ${ref}`
            : "Complaint submitted successfully."
        );
        toast.success("Complaint submitted successfully.");
        setError("");
      } else {
        // Show error message and details if available
        let errorMsg =
          `API ERROR: ${response.error} \n\n` || "Failed to submit complaint.";

        errorMsg += `\n\n, But still we log and sent your complaint by Email to concern department.`;
        setError(errorMsg);
        toast.error(errorMsg);
        setMessage("");
      }
    } catch (err) {
      setError("Unexpected error: " + err.message);
      setMessage("");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
          Suggestions and Complaints
          <p className="text-base text-darkTextColor mt-2 font-poppins font-normal">
            We value your feedback. Please let us know if you have any
            complaints or suggestions to improve our services.
          </p>
        </div>
      </div>

      <div className="flex items-start justify-self-start font-poppins">
        <div className="col-span-full">
          <form onSubmit={handleSubmit}>
            {/* add description text box */}
            <label
              htmlFor="description"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Description
            </label>
            <div className="mt-2">
              <textarea
                id="description"
                name="description"
                rows={4}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                defaultValue={""}
              />
            </div>

            <label
              htmlFor="cover-photo"
              className="block text-sm font-medium mt-4 leading-6 text-gray-900"
            >
              Upload Image (Optional)
            </label>
            <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
              <div className="text-center">
                <PhotoIcon
                  aria-hidden="true"
                  className="mx-auto h-12 w-12 text-gray-300"
                />
                <div className="mt-4 flex text-sm leading-6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <p className="font-poppins text-primary hover:text-primaryHover">
                      Upload a file
                    </p>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs leading-5 text-gray-600">
                  PNG, JPG up to 2MB
                </p>
              </div>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            <div className="mt-6">
              <button
                type="submit"
                className="w-full px-4 py-2 text-sm font-medium leading-5 text-textWhite bg-secondary hover:bg-secondaryHover border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Submit
              </button>
            </div>
            {message && (
              <p className="text-green-500 text-xs mt-2">{message}</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
